import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { PhoneOutgoing, PhoneCall, Shield, Sparkles, Loader2, Hash, RefreshCw, KeyRound, ArrowRightLeft, PhoneForwarded } from "lucide-react";
import { Input } from "../ui/input";
import axios from "axios";
import { toast } from "sonner";

const CallMaskingTab = ({
  callMaskingSettings, setCallMaskingSettings,
  callMaskingLoading, setCallMaskingLoading,
  phoneNumbers, limits, API, getAuthConfig,
}) => {
  const [activePins, setActivePins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(false);
  const [changingPhone, setChangingPhone] = useState(false);

  const saveSettings = async (updates) => {
    setCallMaskingLoading(true);
    try {
      await axios.put(`${API}/call-masking/settings`, { ...callMaskingSettings, ...updates }, getAuthConfig());
      setCallMaskingSettings(prev => ({ ...prev, ...updates }));
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
      return false;
    } finally {
      setCallMaskingLoading(false);
    }
  };

  const fetchActivePins = useCallback(async () => {
    setPinsLoading(true);
    try {
      const res = await axios.get(`${API}/call-masking/active-pins`, getAuthConfig());
      setActivePins(res.data.pins || []);
    } catch {
      setActivePins([]);
    } finally {
      setPinsLoading(false);
    }
  }, [API, getAuthConfig]);

  useEffect(() => {
    if (callMaskingSettings.enabled) fetchActivePins();
  }, [callMaskingSettings.enabled, fetchActivePins]);

  const maskingPhone = callMaskingSettings.masking_phone;
  const maskingPhoneNumber = maskingPhone?.phone_number;
  const availableNumbers = callMaskingSettings.available_numbers || phoneNumbers || [];

  const handleSetMaskingPhone = async (numberId) => {
    const prevMasking = availableNumbers.find(p => p.purpose === "call_masking");
    if (prevMasking && prevMasking.number_id !== numberId) {
      try { await axios.put(`${API}/settings/phone-numbers/${prevMasking.number_id}`, { purpose: "business" }, getAuthConfig()); } catch {}
    }
    try {
      await axios.put(`${API}/settings/phone-numbers/${numberId}`, { purpose: "call_masking" }, getAuthConfig());
      const phone = availableNumbers.find(p => p.number_id === numberId);
      if (phone) await saveSettings({ default_caller_id: phone.phone_number });
      toast.success("Call masking phone number assigned");
      setChangingPhone(false);
      try {
        const res = await axios.get(`${API}/call-masking/settings`, getAuthConfig());
        setCallMaskingSettings(res.data);
      } catch {}
    } catch {
      toast.error("Failed to assign masking phone number");
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return d; }
  };

  const statusColor = (s) => {
    const map = { scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30", in_progress: "bg-amber-500/20 text-amber-400 border-amber-500/30", pending: "bg-purple-500/20 text-purple-400 border-purple-500/30" };
    return map[s] || "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
  };

  return (
    <div className="space-y-6" data-testid="call-masking-settings-card">
      {/* Main Settings Card */}
      <Card className="glass-card border-theme">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                <PhoneOutgoing className="w-5 h-5 text-[#FFCC00]" /> Call Masking
              </CardTitle>
              <CardDescription className="text-muted-foreground">PIN-based masked calling — technicians call a central number, enter a PIN, and connect to customers</CardDescription>
            </div>
            {!limits?.features?.call_masking && (
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                <Sparkles className="w-3 h-3 mr-1" /> Premium Feature
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-4 bg-theme-surface rounded-lg border border-theme">
            <div className="space-y-1">
              <Label className="text-foreground font-medium">Enable Call Masking</Label>
              <p className="text-muted-foreground text-sm">When enabled, technicians use a PIN to call customers via a central number</p>
            </div>
            <Switch
              checked={callMaskingSettings.enabled}
              onCheckedChange={async (checked) => {
                if (!limits?.features?.call_masking && checked) { toast.error("Call masking is not available on your current plan. Please upgrade."); return; }
                const success = await saveSettings({ enabled: checked });
                if (success) toast.success(checked ? "Call masking enabled" : "Call masking disabled");
              }}
              disabled={callMaskingLoading || (!limits?.features?.call_masking)}
              className="data-[state=checked]:bg-[#FFCC00]"
              data-testid="call-masking-toggle"
            />
          </div>

          {/* Masking Phone Number */}
          <div className="space-y-2">
            <Label className="text-foreground flex items-center gap-2">
              <Hash className="w-4 h-4 text-[#FFCC00]" /> Call Masking Phone Number
            </Label>
            {maskingPhone && !changingPhone ? (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-emerald-400 font-mono text-sm">{maskingPhoneNumber}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{maskingPhone.label || "Masking Line"} — techs call this number and enter their PIN</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Active</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChangingPhone(true)}
                    className="text-muted-foreground hover:text-foreground h-7 px-2"
                    data-testid="change-masking-phone-btn"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Change
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Select
                  onValueChange={handleSetMaskingPhone}
                  disabled={!callMaskingSettings.enabled || callMaskingLoading}
                >
                  <SelectTrigger className="bg-theme-surface border-theme text-foreground" data-testid="masking-phone-select">
                    <SelectValue placeholder="Select a phone number to use for call masking" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-theme">
                    {availableNumbers
                      .filter(p => p.purpose !== "call_masking")
                      .map((phone) => (
                        <SelectItem key={phone.number_id || phone.phone_number} value={phone.number_id}>
                          {phone.label || "Business"} — {phone.phone_number}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center justify-between">
                  <p className="text-orange-400 text-xs">
                    {changingPhone ? "Select a new phone number to replace the current masking line." : "No masking phone assigned. Select a purchased phone number to use as the central call masking line."}
                  </p>
                  {changingPhone && (
                    <Button variant="ghost" size="sm" onClick={() => setChangingPhone(false)} className="text-muted-foreground text-xs h-6 px-2">
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Record Calls */}
          <div className="flex items-center justify-between p-4 bg-theme-surface rounded-lg border border-theme">
            <div className="space-y-1">
              <Label className="text-foreground font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400" /> Record Calls
              </Label>
              <p className="text-muted-foreground text-sm">Record masked calls for quality assurance (check local laws)</p>
            </div>
            <Switch
              checked={callMaskingSettings.record_calls}
              onCheckedChange={async (checked) => {
                const success = await saveSettings({ record_calls: checked });
                if (success) toast.success(checked ? "Call recording enabled" : "Call recording disabled");
              }}
              disabled={!callMaskingSettings.enabled || callMaskingLoading}
              className="data-[state=checked]:bg-red-500"
              data-testid="record-calls-toggle"
            />
          </div>

          {/* Fallback Number */}
          <div className="space-y-2 p-4 bg-theme-surface rounded-lg border border-theme">
            <Label className="text-foreground font-medium flex items-center gap-2">
              <PhoneForwarded className="w-4 h-4 text-blue-400" /> Fallback Number
            </Label>
            <p className="text-muted-foreground text-sm">When a customer calls back and the technician is unavailable, route to this number (e.g., office phone)</p>
            <Input
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={callMaskingSettings.fallback_number || ""}
              onChange={(e) => setCallMaskingSettings(prev => ({ ...prev, fallback_number: e.target.value }))}
              disabled={!callMaskingSettings.enabled || callMaskingLoading}
              className="bg-theme-surface border-theme text-foreground font-mono"
              data-testid="fallback-number-input"
            />
          </div>

          <Button
            onClick={async () => {
              const success = await saveSettings(callMaskingSettings);
              if (success) toast.success("Call masking settings saved");
            }}
            disabled={callMaskingLoading}
            className="w-full bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90"
            data-testid="save-masking-btn"
          >
            {callMaskingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Call Masking Settings
          </Button>

          {/* How It Works */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
              <PhoneCall className="w-4 h-4" /> How Call Masking Works
            </h4>
            <ol className="text-muted-foreground text-sm space-y-2 list-decimal list-inside">
              <li><strong>Outbound:</strong> Technician calls the masking number, enters PIN, and is connected to the customer. Customer sees your business number.</li>
              <li><strong>Inbound Callback:</strong> When a customer calls the masked number back, the system automatically identifies them and routes to the assigned technician.</li>
              <li>If the technician is unavailable, the call routes to the configured fallback number.</li>
              <li>PINs are auto-generated when jobs are assigned and deactivated on completion.</li>
              <li>All calls are logged in the job history for tracking.</li>
            </ol>
            <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-xs">
              SMS Template Variable: <code className="bg-yellow-500/10 px-1 rounded">{"{{masked_call_number_with_pin}}"}</code> — use in assignment SMS to include the formatted number with PIN
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active PINs Card */}
      {callMaskingSettings.enabled && (
        <Card className="glass-card border-theme" data-testid="active-pins-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-base">
                  <KeyRound className="w-5 h-5 text-[#FFCC00]" /> Active PINs
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Jobs with active call masking PINs ({activePins.length})
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchActivePins}
                disabled={pinsLoading}
                className="text-muted-foreground hover:text-foreground h-8 px-2"
                data-testid="refresh-pins-btn"
              >
                <RefreshCw className={`w-4 h-4 ${pinsLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pinsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading active PINs...
              </div>
            ) : activePins.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No active PINs. PINs are generated when jobs are assigned to technicians.
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs text-muted-foreground font-medium border-b border-theme">
                  <div className="col-span-2">PIN</div>
                  <div className="col-span-2">Job</div>
                  <div className="col-span-3">Customer</div>
                  <div className="col-span-3">Technician</div>
                  <div className="col-span-2">Status</div>
                </div>
                {activePins.map((pin) => (
                  <div
                    key={pin.job_id}
                    className="grid grid-cols-12 gap-2 px-3 py-2.5 bg-theme-surface rounded-lg border border-theme items-center hover:border-[#FFCC00]/30 transition-colors"
                    data-testid={`pin-row-${pin.call_mask_pin}`}
                  >
                    <div className="col-span-2">
                      <span className="font-mono text-[#FFCC00] font-bold text-sm tracking-wider">{pin.call_mask_pin}</span>
                    </div>
                    <div className="col-span-2 text-foreground text-sm truncate" title={pin.title}>
                      {pin.job_display_id || pin.job_id?.slice(0, 8)}
                    </div>
                    <div className="col-span-3 text-foreground text-sm truncate" title={pin.customer_name}>
                      {pin.customer_name || "—"}
                    </div>
                    <div className="col-span-3 text-muted-foreground text-sm truncate" title={pin.assigned_to_name}>
                      {pin.assigned_to_name || "—"}
                    </div>
                    <div className="col-span-2">
                      <Badge className={`text-[10px] px-1.5 py-0 ${statusColor(pin.status)}`}>
                        {(pin.status || "").replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CallMaskingTab;
